'use client';

import 'react-grid-layout/css/styles.css';

import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import type { PropsWithChildren } from 'react';
import { useCallback, useMemo, useRef } from 'react';
import type { GridLayoutProps } from 'react-grid-layout';
import { GridLayout, verticalCompactor } from 'react-grid-layout';

import { SymbolContext } from '@flipster/features/src/shared/symbol/bunja';
import XCircleIcon from '@flipster/react/icon/generated/XCircleIcon';
import { useMeasure } from '@flipster/react/misc/browser/measure';
import { useBreakpoint } from '@flipster/react/misc/responsive';
import { css, cx } from '@flipster/styled-system/css';
import { circle, grid, stack } from '@flipster/styled-system/patterns';
import { token } from '@flipster/styled-system/tokens';

import ChartAndInfo from '../chart-and-info';
import { EtcSection } from '../etc-section';
import FomoBanner from '../fomo-banner';
import { OrderForm } from '../order-form';
import { Orderbook } from '../orderbook';
import { orderbookStateBunja } from '../state';
import { tradeSymbolBunja } from '../symbol';
import { SymbolInfo } from '../symbol-info';
import { gridCols, gridMargin, gridRowHeight } from './constants';
import { tradeLayoutBunja } from './state';
import type { PanelId } from './types';
import { useGridLayout } from './use-grid-layout';

const panelHoverOverlayClass = 'panel-hover-overlay';

export function TradePanelLayout() {
  const { layoutStateAtom, updateLayouts } = useBunja(tradeLayoutBunja);
  const { isShowOrderbookAtom } = useBunja(orderbookStateBunja);
  const layoutState = useAtomValue(layoutStateAtom);
  const isShowOrderbook = useAtomValue(isShowOrderbookAtom);
  // Measures the left-area width for react-grid-layout's `width` prop.
  // Only attached in the desktop branch (on the left-area div).
  const [measureRef, { width: gridWidth }] = useMeasure<HTMLDivElement>();
  const gridContainerRef = useRef<HTMLDivElement | null>(null);
  const gridMounted = gridWidth > 0;
  const { breakpoint, isMobile } = useBreakpoint();

  const setGridContainerRef = useCallback(
    (node: HTMLDivElement | null) => {
      gridContainerRef.current = node;
      measureRef(node);
    },
    [measureRef],
  );

  // Use a class toggle instead of React state to avoid re-rendering all panels during drag/resize.
  const setInteracting = useCallback((active: boolean) => {
    gridContainerRef.current?.classList.toggle('grid-interacting', active);
  }, []);

  const visibleLayouts = useMemo(() => {
    return layoutState.layouts.filter((l) => !layoutState.hiddenPanels.includes(l.i as PanelId));
  }, [layoutState.layouts, layoutState.hiddenPanels]);

  const { renderLayouts, handleLayoutChange } = useGridLayout({
    visibleLayouts,
    gridWidth,
    gridMounted,
    updateLayouts,
    isShowOrderbook,
  });

  type GridEventCallback = NonNullable<GridLayoutProps['onDragStop']>;

  const onDragStart = useCallback<GridEventCallback>(() => setInteracting(true), [setInteracting]);
  const onDragStop = useCallback<GridEventCallback>(() => setInteracting(false), [setInteracting]);
  const onResizeStart = useCallback<GridEventCallback>(() => setInteracting(true), [setInteracting]);
  const onResizeStop = useCallback<GridEventCallback>(() => setInteracting(false), [setInteracting]);

  // react-grid-layout only at tablet
  // SSR + mobile + tablet use the original CSS Grid layout (matches develop).
  if (!breakpoint || isMobile) {
    return (
      <TradeSymbolProvider>
        <div
          className={grid({
            gridTemplateAreas: {
              base: '"symbol-info" "chart" "etc" "order-form" "fomo-banner"',
              tablet: isShowOrderbook
                ? `
               "symbol-info symbol-info order-form"
               "chart       orderbook   order-form"
               "etc         etc         order-form"
               "fomo-banner fomo-banner fomo-banner"`
                : `
               "symbol-info symbol-info order-form"
               "chart       chart       order-form"
               "etc         etc         order-form"
               "fomo-banner fomo-banner fomo-banner"`,
            },
            gridTemplateColumns: { tablet: isShowOrderbook ? '1fr 320px 360px' : '1fr 360px' },
            gridTemplateRows: {
              base: 'min-content max-content',
              tablet: 'var(--symbol-info-height) 590px 1fr var(--fomo-banner-height)',
            },
            gridGap: 'var(--grid-space)',
            p: 'var(--grid-space)',
            minH: 'minHeightWithoutNavigation',
            '& > *': { minW: 0 },
          })}
        >
          <SymbolInfo />
          <ChartAndInfo />
          {isShowOrderbook && <Orderbook />}
          <EtcSection />
          <OrderForm />
          <FomoBanner />
        </div>
      </TradeSymbolProvider>
    );
  }

  // Desktop: react-grid-layout for the left area, OrderForm + FomoBanner outside
  // Named grid areas must match OrderForm (`gridArea: 'order-form'`) and
  // FomoBanner (`gridArea: 'fomo-banner'`) defined in their own components.
  return (
    <TradeSymbolProvider>
      <div
        className={grid({
          gridTemplateAreas: `"panels order-form" "fomo-banner fomo-banner"`,
          gridTemplateColumns: '1fr 360px',
          gridTemplateRows: '1fr var(--fomo-banner-height)',
          gridColumnGap: 'var(--grid-space)',
          p: 'var(--grid-space)',
          minH: 'minHeightWithoutNavigation',
          '& > *': { minW: 0 },
        })}
      >
        <div
          ref={setGridContainerRef}
          className={css({
            gridArea: 'panels',
            pos: 'relative',
            // Negative margin on left/top/bottom to fill the outer container's padding.
            // Right side intentionally excluded to respect gridColumnGap with order-form.
            m: 'calc(-1 * var(--grid-space))',
            zIndex: 1,
            // When grid-interacting class is toggled (via DOM, no React re-render),
            // disable pointer-events on panel content to prevent iframes/inputs from capturing mouse
            '&.grid-interacting .panel-content': { pointerEvents: 'none' },
            '& [data-drag-handle]': { cursor: 'grab' },
            '& .react-grid-placeholder': {
              bg: 'rgba(10, 132, 255, 0.4)!',
              opacity: '1!',
              rounded: 'var(--grid-rounded)',
            },
          })}
        >
          {gridMounted && (
            <GridLayout
              layout={renderLayouts}
              gridConfig={{
                cols: gridCols,
                rowHeight: gridRowHeight,
                margin: gridMargin,
              }}
              width={gridWidth}
              compactor={verticalCompactor}
              dragConfig={{
                handle: '[data-drag-handle]',
                cancel: 'button, a, input, select, textarea, [data-no-drag]',
              }}
              resizeConfig={{
                handles: ['se'],
              }}
              onLayoutChange={handleLayoutChange}
              onDragStart={onDragStart}
              onDragStop={onDragStop}
              onResizeStart={onResizeStart}
              onResizeStop={onResizeStop}
            >
              {renderLayouts.map((item) => {
                const panelId = item.i as PanelId;
                return (
                  <div
                    key={panelId}
                    className={stack({
                      pos: 'relative',
                      h: '100%',
                      rounded: 'var(--grid-rounded)',
                      // Show close button and resize handle on hover
                      [`& > .${panelHoverOverlayClass}`]: { opacity: 0, transition: 'opacity 0.15s' },
                      [`&:hover > .${panelHoverOverlayClass}`]: { opacity: 1 },
                    })}
                  >
                    <GridPanelContent panelId={panelId}>
                      {panelId === 'symbolInfo' && <SymbolInfo />}
                      {panelId === 'chart' && <ChartAndInfo />}
                      {panelId === 'orderbook' && <Orderbook />}
                      {panelId === 'etcSection' && <EtcSection />}
                    </GridPanelContent>
                  </div>
                );
              })}
            </GridLayout>
          )}
        </div>
        {/* OrderForm and FomoBanner: grid areas match their internal gridArea values.
          When FomoBanner returns null, OrderForm becomes :last-child → height adjusts. */}
        <OrderForm />
        <FomoBanner />
      </div>
    </TradeSymbolProvider>
  );
}

interface GridPanelContentProps extends PropsWithChildren {
  panelId: PanelId;
}
const GridPanelContent = ({ panelId, children }: GridPanelContentProps) => {
  const { closePanel } = useBunja(tradeLayoutBunja);
  return (
    <>
      <button
        type='button'
        className={cx(panelHoverOverlayClass, circle({ pos: 'absolute', top: 0, right: 0, zIndex: 10 }))}
        onClick={() => closePanel(panelId)}
      >
        <XCircleIcon size={14} color={token('colors.content.default.level-1')} />
      </button>
      <span
        data-no-drag
        className={cx(
          panelHoverOverlayClass,
          css({
            zIndex: 10,
            pos: 'absolute',
            bottom: 0,
            right: 0,
            w: 12,
            h: 12,
            borderBottomWidth: '3px',
            borderBottomStyle: 'solid',
            borderRightWidth: '3px',
            borderRightStyle: 'solid',
            borderColor: 'content.default.level-1',
            pointerEvents: 'none',
          }),
        )}
      />
      <div
        className={cx(
          'panel-content',
          stack({
            flex: 1,
            minH: 0,
            minW: 0,
            // Reset margins that child components set for CSS Grid spacing
            '& > *': { m: 0 },
          }),
        )}
      >
        {children}
      </div>
    </>
  );
};

const TradeSymbolProvider = ({ children }: PropsWithChildren) => {
  const { symbol } = useBunja(tradeSymbolBunja);
  return <SymbolContext value={symbol}>{children}</SymbolContext>;
};
